export const requiredBetaPublicRoutes = [
  '/download',
  '/demo',
  '/beta',
  '/account',
  '/account/credits',
  '/account/activation-code',
  '/turtle-soup',
  '/resources',
  '/changelog',
  '/help/installer',
  '/help/privacy',
  '/help/refund',
  '/legal/privacy',
  '/legal/terms',
  '/legal/third-party-notices',
] as const

export type BetaPublicRoute = typeof requiredBetaPublicRoutes[number]
export type BetaPublicPageRoute = BetaPublicRoute | '/'
export type BetaPublicLocale = 'zh-Hans' | 'en'
export type BetaPublicPageKind = 'home' | 'demo' | 'download' | 'beta' | 'account' | 'feature' | 'changelog' | 'support' | 'legal'

export const betaPublicLocales = ['zh-Hans', 'en'] as const satisfies readonly BetaPublicLocale[]

export interface BetaPublicNavigationItem {
  label: string
  to: BetaPublicPageRoute
}

export interface BetaPublicAction {
  label: string
  to?: BetaPublicRoute | '/auth/login' | '/'
  href?: string
  icon: string
}

export interface BetaPublicPanel {
  title: string
  body: string
  icon: string
}

export interface BetaPublicPage {
  label: string
  eyebrow: string
  title: string
  summary: string
  body: string[]
  actions?: BetaPublicAction[]
  panels?: BetaPublicPanel[]
  notes?: string[]
}

export interface BetaPublicCopy {
  ui: {
    brand: string
    domain: string
    languageLabel: string
    regionLanguageNote: string
    releaseBadge: string
    qaBadge: string
    sectionTitle: string
  }
  navigation: readonly BetaPublicNavigationItem[]
  pages: Record<BetaPublicPageRoute, BetaPublicPage>
}

export interface BetaPublicAuthCopy {
  backHome: string
  title: string
  summary: string
  displayName: string
  displayNamePlaceholder: string
  email: string
  password: string
  passwordPlaceholder: string
  signIn: string
  signUp: string
  createAccount: string
  useExistingAccount: string
  continueWith: string
  termsPrefix: string
  terms: string
  privacy: string
}

export const betaPublicShellRoutes = [
  '/',
  ...requiredBetaPublicRoutes,
  '/auth/login',
] as const

export const betaPublicAuthContent = {
  'zh-Hans': {
    backHome: '返回官网',
    title: '登录账号',
    summary: '登录后可以使用官方云模型、查看点数和管理内测权益。本地聊天与记忆不会因为登录自动上传。',
    displayName: '显示名称',
    displayNamePlaceholder: '怎么称呼你',
    email: '邮箱',
    password: '密码',
    passwordPlaceholder: '至少 8 个字符',
    signIn: '邮箱登录',
    signUp: '创建账号',
    createAccount: '注册新账号',
    useExistingAccount: '使用已有账号',
    continueWith: '其他登录方式',
    termsPrefix: '继续即表示你同意',
    terms: '服务条款',
    privacy: '隐私政策',
  },
  'en': {
    backHome: 'Back to site',
    title: 'Sign in',
    summary: 'Sign in to use the official cloud, review credits, and manage Beta access. Local chats and memory are not uploaded just because you sign in.',
    displayName: 'Display name',
    displayNamePlaceholder: 'How should we address you?',
    email: 'Email',
    password: 'Password',
    passwordPlaceholder: 'At least 8 characters',
    signIn: 'Sign in with email',
    signUp: 'Create account',
    createAccount: 'Create a new account',
    useExistingAccount: 'Use an existing account',
    continueWith: 'Other sign-in options',
    termsPrefix: 'By continuing, you agree to the',
    terms: 'Terms',
    privacy: 'Privacy Policy',
  },
} as const satisfies Record<BetaPublicLocale, BetaPublicAuthCopy>

const betaPublicShellRouteSet = new Set<string>(betaPublicShellRoutes)

const betaPublicPageKinds: Record<BetaPublicPageRoute, BetaPublicPageKind> = {
  '/': 'home',
  '/download': 'download',
  '/demo': 'demo',
  '/beta': 'beta',
  '/account': 'account',
  '/account/credits': 'account',
  '/account/activation-code': 'account',
  '/turtle-soup': 'feature',
  '/resources': 'feature',
  '/changelog': 'changelog',
  '/help/installer': 'support',
  '/help/privacy': 'support',
  '/help/refund': 'support',
  '/legal/privacy': 'legal',
  '/legal/terms': 'legal',
  '/legal/third-party-notices': 'legal',
}

function normalizeRoutePath(path: string): string {
  const pathOnly = path.split(/[?#]/)[0] || '/'
  return pathOnly.length > 1 ? pathOnly.replace(/\/+$/, '') : pathOnly
}

export function isBetaPublicRoutePath(path: string): boolean {
  return betaPublicShellRouteSet.has(normalizeRoutePath(path))
}

export function shouldInitializeStageRuntimeForRoute(path: string): boolean {
  return !isBetaPublicRoutePath(path)
}

function isBetaPublicLocale(locale: string | null | undefined): locale is BetaPublicLocale {
  return locale === 'zh-Hans' || locale === 'en'
}

export function detectBetaPublicLocale(input: {
  languages?: readonly string[]
  language?: string
  timeZone?: string
} = {}): BetaPublicLocale {
  const candidates = [
    ...(input.languages ?? []),
    input.language,
  ].filter(Boolean).map(language => language!.toLowerCase())

  if (candidates.some(language => language.startsWith('zh')))
    return 'zh-Hans'

  if (input.timeZone === 'Asia/Shanghai' || input.timeZone === 'Asia/Chongqing')
    return 'zh-Hans'

  return 'en'
}

export function getBrowserBetaPublicLocale(): BetaPublicLocale {
  if (typeof window === 'undefined')
    return 'zh-Hans'

  const saved = window.localStorage.getItem('public-beta/locale')
  if (isBetaPublicLocale(saved))
    return saved

  return detectBetaPublicLocale({
    languages: navigator.languages,
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
}

export function getBetaPublicPageKind(routePath: BetaPublicPageRoute): BetaPublicPageKind {
  return betaPublicPageKinds[routePath]
}

const zhNavigation = [
  { label: '首页', to: '/' },
  { label: '下载', to: '/download' },
  { label: '演示', to: '/demo' },
  { label: '内测', to: '/beta' },
  { label: '海龟汤', to: '/turtle-soup' },
  { label: '角色资源', to: '/resources' },
  { label: '更新', to: '/changelog' },
  { label: '帮助', to: '/help/installer' },
  { label: '隐私', to: '/legal/privacy' },
  { label: '条款', to: '/legal/terms' },
  { label: '退款与客服', to: '/help/refund' },
  { label: '账户', to: '/account' },
] as const satisfies readonly BetaPublicNavigationItem[]

const enNavigation = [
  { label: 'Home', to: '/' },
  { label: 'Download', to: '/download' },
  { label: 'Demo', to: '/demo' },
  { label: 'Beta Access', to: '/beta' },
  { label: 'Turtle Soup', to: '/turtle-soup' },
  { label: 'Characters & Resources', to: '/resources' },
  { label: 'Changelog', to: '/changelog' },
  { label: 'Help', to: '/help/installer' },
  { label: 'Privacy', to: '/legal/privacy' },
  { label: 'Terms', to: '/legal/terms' },
  { label: 'Refund & Support', to: '/help/refund' },
  { label: 'Account', to: '/account' },
] as const satisfies readonly BetaPublicNavigationItem[]

export const betaPublicContent = {
  'zh-Hans': {
    ui: {
      brand: 'Wuwiii Beta',
      domain: 'wuwiii.cn',
      languageLabel: '语言',
      regionLanguageNote: '已根据浏览器语言和地区自动选择，可手动切换。',
      releaseBadge: '小规模内测',
      qaBadge: '当前为未签名 QA 包',
      sectionTitle: '现在可以验证的内容',
    },
    navigation: zhNavigation,
    pages: {
      '/': {
        label: '首页',
        eyebrow: 'AI 角色，与日常同屏',
        title: '让喜欢的角色，住进你的桌面',
        summary: '能聊天、说话、记住相处细节，也懂得在你忙时安静待着。桌面角色、快捷聊天和多角色互动集中在一个应用里。',
        body: [
          '从桌面边缘打开快捷聊天，不必先进入复杂界面。文字、语音、提醒和角色动作会围绕同一个角色持续衔接。',
          '每张角色卡可以保存独立的人格、记忆范围、常用声音和外观模型；切换角色时，对话方式和舞台形象也会一起变化。',
          '聊天记录、长期记忆、本地路径和用户自己的服务密钥默认保留在设备上。只有主动使用云端能力时，才会发送完成当前请求所需的内容。',
        ],
        actions: [
          { label: '查看互动演示', to: '/demo', icon: 'i-solar:play-circle-bold-duotone' },
          { label: '下载 Wuwiii 1.0', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: '了解隐私边界', to: '/help/privacy', icon: 'i-solar:shield-user-bold-duotone' },
        ],
        panels: [
          {
            title: '随时开口',
            body: '在桌面边缘快速发消息、使用语音，角色回复不必打断当前工作。',
            icon: 'i-solar:monitor-smartphone-bold-duotone',
          },
          {
            title: '角色有自己的声音',
            body: '人格、记忆、语音和外观跟随角色卡保存，不同角色不必共用同一种表达。',
            icon: 'i-solar:volume-loud-bold-duotone',
          },
          {
            title: '本地优先',
            body: '私密对话和长期记忆默认留在设备上，云端使用范围保持清楚可查。',
            icon: 'i-solar:shield-user-bold-duotone',
          },
        ],
      },
      '/download': {
        label: '下载',
        eyebrow: '正式版 · 1.0',
        title: '下载 Wuwiii 1.0',
        summary: '选择 Windows 安装程序、便携压缩包或 Android 安装包。桌面与手机均为 1.0 版本。',
        body: [
          'Windows 安装程序：Wuwiii-1.0-windows-x64-setup.exe；便携版：Wuwiii-1.0-windows-x64-portable.zip；Android：Wuwiii-1.0-release.apk。',
          'Windows 安装程序 SHA-256：7CDA5249B483BE351C376D03B9884A3790336AE3D7EB0CF685B9006B9AADB65A；便携版 SHA-256：BE50E6254F8E5373035853FFEB8BFF310D9A8AAC1D658702A979456AAFAEEC8F。Android 校验值见下载目录中的 android-beta.json。',
          'Windows 安装程序使用项目自签名证书，系统可能显示未知发布者。Android 沿用此前的签名，可覆盖安装旧版。',
          '如需安装、卸载或恢复帮助，可查看安装帮助。',
        ],
        actions: [
          { label: '下载 Windows 安装程序', href: '/downloads/Wuwiii-1.0-windows-x64-setup.exe', icon: 'i-solar:download-square-bold-duotone' },
          { label: '下载 Windows 便携版', href: '/downloads/Wuwiii-1.0-windows-x64-portable.zip', icon: 'i-solar:archive-bold-duotone' },
          { label: '下载 Android 安装包', href: '/downloads/Wuwiii-1.0-release.apk', icon: 'i-solar:smartphone-bold-duotone' },
          { label: '安装帮助', to: '/help/installer', icon: 'i-solar:shield-check-bold-duotone' },
          { label: '更新记录', to: '/changelog', icon: 'i-solar:document-text-bold-duotone' },
        ],
        notes: [
          'Windows 为项目自签名安装包；安装前请核对 SHA-256。',
        ],
      },
      '/demo': {
        label: '演示',
        eyebrow: '真实交互预览',
        title: '她不只回答，也会在桌面陪你',
        summary: '打开快捷聊天，说一句话，角色就会在当前场景里回应。想继续聊可以展开完整对话，忙起来也能让她安静待在一旁。',
        body: [
          '本页只切换预设对话，旨在展示桌面布局和角色反馈；不会读取本地文件、上传聊天内容或消耗点数。',
          '桌面端支持快捷聊天、完整对话、语音和提醒，角色会沿用对应人格卡的声音、记忆范围与外观。',
          '单角色对话和多角色群聊使用同一套角色资料，头像、语音和记忆边界会跟随参与角色。',
        ],
        actions: [
          { label: '下载桌面端', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: '查看内测说明', to: '/beta', icon: 'i-solar:ticket-sale-bold-duotone' },
        ],
        panels: [
          {
            title: '快捷气泡',
            body: '不用先打开完整聊天窗口，在桌面边缘就能发出一句话并接收回应。',
            icon: 'i-solar:chat-round-dots-bold-duotone',
          },
          {
            title: '语音与角色同步',
            body: '角色卡会记住最近使用的语音厂商、模型和声音，切换角色时一起切换。',
            icon: 'i-solar:alarm-play-bold-duotone',
          },
          {
            title: '对话各自延续',
            body: '不同人格保留各自的记忆范围，群聊也会明确记录由哪个角色回应。',
            icon: 'i-solar:users-group-rounded-bold-duotone',
          },
        ],
      },
      '/beta': {
        label: '内测',
        eyebrow: '小规模内测',
        title: '小规模内测，先把体验做稳',
        summary: '当前只开放少量内测名额，安装、点数和模型调用问题都可以得到人工跟进。',
        body: [
          '内测版用于验证桌面互动、官方云点数、语音、提醒、角色资源与多人对话是否稳定。',
          '遇到安装失败、异常扣点或模型调用问题，可以通过公开客服渠道提交账号、订单或使用记录。',
          '正式支付和公开发行准备完成前，下载页会持续标明安装包是否签名以及当前适用范围。',
        ],
        actions: [
          { label: '创建账户', to: '/auth/login', icon: 'i-solar:user-plus-bold-duotone' },
          { label: '退款与客服', to: '/help/refund', icon: 'i-solar:hand-money-bold-duotone' },
        ],
      },
      '/account': {
        label: '账户',
        eyebrow: '账户与点数',
        title: '登录、点数和内测资格',
        summary: '账户用于登录、点数、兑换码、权益和官方云用量记录，不默认接管本地私有内容。',
        body: [
          '服务器侧保存账号、会话、点数、流水、兑换码、权益和官方云使用事件。',
          '本地聊天、长期记忆、关系状态、本地路径和用户 API Key 默认不上传。',
        ],
        actions: [
          { label: '登录或创建账号', to: '/auth/login', icon: 'i-solar:login-3-bold-duotone' },
          { label: '点数流水', to: '/account/credits', icon: 'i-solar:wallet-money-bold-duotone' },
          { label: '兑换码', to: '/account/activation-code', icon: 'i-solar:ticket-bold-duotone' },
        ],
      },
      '/account/credits': {
        label: '点数',
        eyebrow: '点数流水',
        title: '点数消耗要能看清楚',
        summary: '试用点、赠送点和付费点需要可解释，模型调用和娱乐玩法要留下流水证据。',
        body: [
          '登录后可以查看余额以及最近的充值、兑换、赠送、扣点、退款和人工补偿记录。',
          '每次官方云使用都会留下可核对的流水；发生异常时，客服会根据订单与使用记录处理。',
        ],
        actions: [
          { label: '返回账户', to: '/account', icon: 'i-solar:user-id-bold-duotone' },
          { label: '兑换码', to: '/account/activation-code', icon: 'i-solar:ticket-bold-duotone' },
        ],
      },
      '/account/activation-code': {
        label: '兑换码',
        eyebrow: '内测资格',
        title: '兑换内测资格和点数',
        summary: '兑换码可用于开通内测资格、领取赠送点数或接收客服补偿。',
        body: [
          '兑换码用于开通内测资格、赠送点数或补偿点数。',
          '浏览器端不能暴露管理员令牌，也不能直接修改点数或订单。',
        ],
        actions: [
          { label: '返回账户', to: '/account', icon: 'i-solar:user-id-bold-duotone' },
          { label: '联系客服', to: '/help/refund', icon: 'i-solar:chat-round-call-bold-duotone' },
        ],
      },
      '/turtle-soup': {
        label: '海龟汤',
        eyebrow: 'AI 娱乐',
        title: '去海龟汤里开一局',
        summary: '游戏运行在 game.wuwiii.cn，可从这里直接进入；账号和点数能力上线时会保持清楚的使用说明。',
        body: [
          'game.wuwiii.cn 是当前海龟汤游戏站点，保留独立部署和独立入口。',
          '未来如果加入 AI 主持、提示、复盘或生成题目，这些动作应通过 Wuwiii 网关和点数流水处理，不让网页直接调用上游模型。',
        ],
        actions: [
          { label: '进入海龟汤', href: 'https://game.wuwiii.cn', icon: 'i-solar:gamepad-minimalistic-bold-duotone' },
          { label: '点数说明', to: '/account/credits', icon: 'i-solar:wallet-money-bold-duotone' },
          { label: '隐私说明', to: '/help/privacy', icon: 'i-solar:lock-keyhole-bold-duotone' },
        ],
      },
      '/resources': {
        label: '角色资源',
        eyebrow: '文化资源',
        title: '角色和资源发布必须明确授权',
        summary: '资源页可以做浏览和导入入口，但发布必须让用户确认哪些内容会公开。',
        body: [
          '允许公开的字段包括展示名、简介、标签、公开头像或封面、用户选择公开的人设文本、资源文件、许可信息、作者展示名和版本。',
          '禁止默认发布聊天记录、长期记忆、关系状态、API Key、apiBaseUrl、本地文件路径、私密备注、服务商密钥和未审核能力配置。',
        ],
        actions: [
          { label: '隐私帮助', to: '/help/privacy', icon: 'i-solar:shield-user-bold-duotone' },
          { label: '第三方说明', to: '/legal/third-party-notices', icon: 'i-solar:document-text-bold-duotone' },
        ],
      },
      '/changelog': {
        label: '更新',
        eyebrow: '更新记录',
        title: '2026 年 7 月 20 日更新',
        summary: '账户、官方云和桌面快捷聊天体验完成一轮集中更新。',
        body: [
          '账户页新增点数刷新，登录状态或兑换完成后可以立即同步最新可用点数。',
          '官方云模型链路已升级并迁移到香港节点，改善连接稳定性和错误提示。',
          '快捷聊天现在区分收起与关闭；展开后已有回复不会消失，下一次输入也会正常显示。',
          'Live2D 回复气泡移动到人物左上区域并减少面部遮挡，同时仍保证气泡不会超出屏幕。',
        ],
        actions: [
          { label: '下载', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: '安装帮助', to: '/help/installer', icon: 'i-solar:shield-check-bold-duotone' },
        ],
      },
      '/help/installer': {
        label: '安装帮助',
        eyebrow: '帮助',
        title: '安装、更新、卸载和恢复',
        summary: '帮助页要面向普通 Windows 用户，不要求用户理解开发环境。',
        body: [
          '从官网下载完整安装包后直接运行即可；安装完成后可以从桌面快捷方式或开始菜单启动。',
          '由于当前 QA 包未签名，小范围测试时必须明确告知 Windows 可能拦截。',
        ],
        actions: [
          { label: '下载', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: '退款与客服', to: '/help/refund', icon: 'i-solar:hand-money-bold-duotone' },
        ],
      },
      '/help/privacy': {
        label: '隐私帮助',
        eyebrow: '帮助',
        title: '本地优先的隐私边界',
        summary: '用户付款前要知道哪些内容留在本地，哪些云端请求会发送必要上下文。',
        body: [
          '聊天记录默认保留在本地。长期记忆默认保留在本地。关系状态默认保留在本地。本地文件路径和 Workbench 项目内容默认不上传。',
          '官方云请求会发送回答当前请求所需的提示词和上下文。账号、点数、流水、权益、兑换码和使用事件会保存在服务器侧，用于登录、计费、客服和风控。',
        ],
        actions: [
          { label: '隐私政策', to: '/legal/privacy', icon: 'i-solar:document-text-bold-duotone' },
          { label: '服务条款', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
      '/help/refund': {
        label: '退款与客服',
        eyebrow: '帮助',
        title: '退款、补偿和人工客服',
        summary: '内测阶段不开放自动退款，所有支付和点数异常都走人工处理。',
        body: [
          'Wuwiii Beta 不开放自动退款。退款和补偿请求通过公开客服人工处理，需要提供账户、订单证据以及相关点数流水或使用记录。',
          '未使用付费点数、失败扣点和模型调用异常由客服人工核查。处理方式包括人工补偿、流水修正或退款处理。',
          '客服邮箱：3222708890@qq.com；QQ：3222708890；微信：Hehaolingdang123。',
          '客服通常在 7 个自然日内确认受理，复杂问题在 15 个自然日内反馈处理结果；适用法律另有强制要求的，以法律要求为准。',
          '点数扣除问题应依据账户流水、usage event traceId 和 Wuwiii 网关退款行为处理，不建议直接改数据库。',
        ],
        actions: [
          { label: '点数流水', to: '/account/credits', icon: 'i-solar:wallet-money-bold-duotone' },
          { label: '服务条款', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
      '/legal/privacy': {
        label: '隐私',
        eyebrow: '法律',
        title: 'Wuwiii Beta 隐私政策',
        summary: '生效日期：2026 年 7 月 25 日。当前 Beta 由 wuwiii.cn 运营联系人负责。',
        body: [
          '聊天记录、长期记忆、关系状态、本地文件路径、用户 API Key 和 Workbench 项目内容默认保留在本地。',
          '服务器侧保存账号、会话、点数、流水、兑换码、权益、订单、官方云使用事件、用户明确发布的公开资源、审核记录和用户主动提交的客服诊断信息。',
          '官方云请求会通过 Wuwiii 网关发送当前请求所需的提示词和必要上下文到 Wuwiii 服务端及上游模型服务。',
          '用户可在应用内清除本地聊天、记忆和设置。申请删除服务端账号数据时，请通过公开客服提交账号标识并完成身份核验；核验后 30 日内删除或匿名化不再必要的数据，并撤销活动会话。',
          '订单、点数流水、安全审计和争议处理记录只在适用法律、财务审计、防欺诈或纠纷处理所要求的必要期限内保留，并限制访问。客服诊断资料在问题关闭后不再继续用于无关目的。',
          '当前公开付费 Beta 仅面向年满 18 周岁的用户。未成年人不得注册或购买本 Beta 服务。隐私或删除请求可联系 3222708890@qq.com。',
        ],
        actions: [
          { label: '隐私帮助', to: '/help/privacy', icon: 'i-solar:shield-user-bold-duotone' },
          { label: '服务条款', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
      '/legal/terms': {
        label: '条款',
        eyebrow: '法律',
        title: 'Wuwiii Beta 服务条款',
        summary: '生效日期：2026 年 7 月 25 日。条款覆盖早期软件、点数、客服、资源发布和可接受使用。',
        body: [
          'Wuwiii Beta 是早期内测软件。功能、价格、点数消耗、模型和可用性可能随内测运营调整。',
          '注册和购买即表示用户已年满 18 周岁，并同意当前隐私政策与退款支持规则。点数仅用于服务内计量，不是货币，不可转让、提现或脱离账号交易。',
          '用户不得把呜异屋用于紧急、医疗、法律、金融等高风险决策。公开资源上传需要遵守版权、许可和审核规则。',
          '禁止利用服务实施违法活动、侵犯他人权益、绕过计费或安全限制、批量滥用接口，或上传无权再分发的角色、声音、模型和素材。违规账号可被限制能力、撤销会话或停止服务，相关操作保留审计记录。',
          '服务故障、失败扣点和订单争议按退款与客服页面处理；适用法律规定的消费者权利不因本条款而排除。',
        ],
        actions: [
          { label: '退款与客服', to: '/help/refund', icon: 'i-solar:hand-money-bold-duotone' },
          { label: '隐私政策', to: '/legal/privacy', icon: 'i-solar:document-text-bold-duotone' },
        ],
      },
      '/legal/third-party-notices': {
        label: '第三方说明',
        eyebrow: '法律',
        title: '第三方资产、库和模型说明',
        summary: '软件代码来源、依赖和用户导入资产适用各自的许可证与使用条件。',
        body: [
          '本项目基于 Project AIRI 的 MIT License 开源代码持续开发，发行包必须保留原始版权声明和 MIT License；各依赖库、字体和图标继续适用其各自许可证。',
          '用户自行导入的角色卡、Live2D/VRM、声音、动作、背景和其他资源不因导入本产品而获得新的授权。用户必须自行确认使用、修改、公开展示和再分发权限。',
          '商业发行包只能内置已经记录来源、作者、许可证、商用权限和再分发权限的资产。许可状态不明确的默认角色、声音或图片不得随商业包发布。',
          '当前审计的 AvatarSample_A 与 AvatarSample_B 由 VRoid Project 提供，文件内许可元数据允许商用和再分发。桃濑日和 Hiyori Free/Pro 的随包说明要求接受 Live2D 适用协议，并对企业规模设有限制；在再分发依据完成归档前，这两个 Hiyori 文件仍处于商业发行暂停状态。',
          '完整第三方清单和对应许可证文本必须与最终安装包版本一同归档；本页不把任何未列明资产默认为可商用。',
        ],
        actions: [
          { label: '角色资源', to: '/resources', icon: 'i-solar:folder-with-files-bold-duotone' },
          { label: '服务条款', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
    },
  },
  'en': {
    ui: {
      brand: 'Wuwiii Beta',
      domain: 'wuwiii.cn',
      languageLabel: 'Language',
      regionLanguageNote: 'Selected from browser language and region. You can switch it manually.',
      releaseBadge: 'Limited Beta',
      qaBadge: 'Unsigned QA installer',
      sectionTitle: 'What is ready to review',
    },
    navigation: enNavigation,
    pages: {
      '/': {
        label: 'Home',
        eyebrow: 'AI characters in your everyday workspace',
        title: 'Bring a favorite character onto your desktop',
        summary: 'Chat, speak, remember shared details, and stay quietly nearby while you work. Desktop characters, quick chat, and multi-character conversations live in one app.',
        body: [
          'Open quick chat from the edge of the desktop instead of leaving your current task. Text, voice, reminders, and character motion stay connected to the same character.',
          'Each character card can keep its own persona, memory scope, preferred voice, and appearance model. Switching characters changes both the conversation style and stage presence.',
          'Chat history, long-term memory, local paths, and user service keys stay on the device by default. Cloud features only receive what is needed for the request you choose to send.',
        ],
        actions: [
          { label: 'View interaction demo', to: '/demo', icon: 'i-solar:play-circle-bold-duotone' },
          { label: 'Download Wuwiii 1.0', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: 'Review privacy boundaries', to: '/help/privacy', icon: 'i-solar:shield-user-bold-duotone' },
        ],
        panels: [
          {
            title: 'Talk without breaking focus',
            body: 'Send a message or use voice from the edge of the desktop without opening a full chat window first.',
            icon: 'i-solar:monitor-smartphone-bold-duotone',
          },
          {
            title: 'A voice for each character',
            body: 'Persona, memory, voice, and appearance stay with the character card instead of being shared by everyone.',
            icon: 'i-solar:volume-loud-bold-duotone',
          },
          {
            title: 'Local first',
            body: 'Private conversations and long-term memory stay on the device by default, with cloud use kept explicit.',
            icon: 'i-solar:shield-user-bold-duotone',
          },
        ],
      },
      '/download': {
        label: 'Download',
        eyebrow: 'Release · 1.0',
        title: 'Download Wuwiii 1.0',
        summary: 'Choose the Windows installer, portable ZIP, or Android APK. Both desktop and mobile are version 1.0.',
        body: [
          'Windows installer: Wuwiii-1.0-windows-x64-setup.exe; portable ZIP: Wuwiii-1.0-windows-x64-portable.zip; Android APK: Wuwiii-1.0-release.apk.',
          'Windows installer SHA-256: 7CDA5249B483BE351C376D03B9884A3790336AE3D7EB0CF685B9006B9AADB65A; portable ZIP SHA-256: BE50E6254F8E5373035853FFEB8BFF310D9A8AAC1D658702A979456AAFAEEC8F. The Android checksum is in android-beta.json.',
          'The Windows installer uses a project self-signed certificate, so Windows may show an unknown-publisher warning. Android keeps the existing signing identity for installation over older builds.',
          'See Installer Help for install, uninstall, and recovery instructions.',
        ],
        actions: [
          { label: 'Windows installer', href: '/downloads/Wuwiii-1.0-windows-x64-setup.exe', icon: 'i-solar:download-square-bold-duotone' },
          { label: 'Windows portable ZIP', href: '/downloads/Wuwiii-1.0-windows-x64-portable.zip', icon: 'i-solar:archive-bold-duotone' },
          { label: 'Android APK', href: '/downloads/Wuwiii-1.0-release.apk', icon: 'i-solar:smartphone-bold-duotone' },
          { label: 'Installer help', to: '/help/installer', icon: 'i-solar:shield-check-bold-duotone' },
          { label: 'Changelog', to: '/changelog', icon: 'i-solar:document-text-bold-duotone' },
        ],
        notes: [
          'Windows uses a project self-signed installer; verify its SHA-256 before installation.',
        ],
      },
      '/demo': {
        label: 'Demo',
        eyebrow: 'Interaction preview',
        title: 'She does more than answer',
        summary: 'Open quick chat and say one thing. The character responds inside the current scene, expands into a full conversation when you want, and stays quietly nearby when you are busy.',
        body: [
          'This page switches between preset lines to preview the desktop layout and character feedback. It does not read local files, upload chats, or spend credits.',
          'The desktop app supports quick chat, full conversations, voice, and reminders while keeping the character card’s voice, memory scope, and appearance together.',
          'Single-character chats and group chats use the same character profiles, so avatars, voices, and memory boundaries follow the participating characters.',
        ],
        actions: [
          { label: 'Download desktop app', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: 'Beta details', to: '/beta', icon: 'i-solar:ticket-sale-bold-duotone' },
        ],
        panels: [
          {
            title: 'Quick chat',
            body: 'Send one line and receive a response from the edge of the desktop before opening the full conversation.',
            icon: 'i-solar:chat-round-dots-bold-duotone',
          },
          {
            title: 'Voice follows the character',
            body: 'Each card remembers its latest speech provider, model, and voice, then restores them when the character returns.',
            icon: 'i-solar:alarm-play-bold-duotone',
          },
          {
            title: 'Conversations stay distinct',
            body: 'Personas keep separate memory scopes, and group chat records exactly which character produced each reply.',
            icon: 'i-solar:users-group-rounded-bold-duotone',
          },
        ],
      },
      '/beta': {
        label: 'Beta Access',
        eyebrow: 'Limited Beta',
        title: 'A limited Beta focused on stability',
        summary: 'Access remains limited while installation, credits, and model calls are monitored with direct support available.',
        body: [
          'The Beta validates desktop interaction, official-cloud credits, voice, reminders, character resources, and multi-character conversations.',
          'Installation failures, unexpected credit charges, and model-call issues can be submitted through the public support channel with account or order details.',
          'Until public payment and release preparation are complete, the download page will continue to state whether each installer is signed and who it is intended for.',
        ],
        actions: [
          { label: 'Create account', to: '/auth/login', icon: 'i-solar:user-plus-bold-duotone' },
          { label: 'Refund support', to: '/help/refund', icon: 'i-solar:hand-money-bold-duotone' },
        ],
      },
      '/account': {
        label: 'Account',
        eyebrow: 'Account',
        title: 'Sign in, credits, and Beta access',
        summary: 'Accounts handle login, credits, activation codes, entitlements, and official-cloud usage records.',
        body: [
          'Server-side data covers account, session, credits, ledger, activation codes, entitlements, and official-cloud usage events.',
          'Local chat, long-term memory, relationship state, local paths, and user API keys are not uploaded by default.',
        ],
        actions: [
          { label: 'Sign in or create account', to: '/auth/login', icon: 'i-solar:login-3-bold-duotone' },
          { label: 'Credits', to: '/account/credits', icon: 'i-solar:wallet-money-bold-duotone' },
          { label: 'Activation code', to: '/account/activation-code', icon: 'i-solar:ticket-bold-duotone' },
        ],
      },
      '/account/credits': {
        label: 'Credits',
        eyebrow: 'Credits',
        title: 'Credit balance and ledger',
        summary: 'Trial, grant, and paid credits should be visible and explainable before and after cloud use.',
        body: [
          'After signing in, you can review the balance and recent top-ups, redemptions, grants, spending, refunds, and support adjustments.',
          'Every official-cloud use leaves a reviewable ledger entry. Support uses order and usage records when something goes wrong.',
        ],
        actions: [
          { label: 'Open account', to: '/account', icon: 'i-solar:user-id-bold-duotone' },
          { label: 'Redeem code', to: '/account/activation-code', icon: 'i-solar:ticket-bold-duotone' },
        ],
      },
      '/account/activation-code': {
        label: 'Activation Code',
        eyebrow: 'Activation',
        title: 'Redeem Beta access and credits',
        summary: 'Activation codes can unlock Beta access, grant credits, or deliver a support adjustment.',
        body: [
          'Activation codes can unlock Beta access, grant credits, or compensate users.',
          'The browser must not expose admin tokens or mutate credits/orders directly.',
        ],
        actions: [
          { label: 'Open account', to: '/account', icon: 'i-solar:user-id-bold-duotone' },
          { label: 'Support', to: '/help/refund', icon: 'i-solar:chat-round-call-bold-duotone' },
        ],
      },
      '/turtle-soup': {
        label: 'Turtle Soup',
        eyebrow: 'AI Entertainment',
        title: 'Start a Turtle Soup round',
        summary: 'The game runs at game.wuwiii.cn and opens directly from here. Account and credit use will remain clearly explained as those features launch.',
        body: [
          'game.wuwiii.cn remains the current Turtle Soup game site with its own deployment and entry point.',
          'If AI host, hints, review, or puzzle generation are added later, those actions should use the Wuwiii gateway and credit ledger instead of calling upstream model providers directly.',
        ],
        actions: [
          { label: 'Open Turtle Soup', href: 'https://game.wuwiii.cn', icon: 'i-solar:gamepad-minimalistic-bold-duotone' },
          { label: 'Credits', to: '/account/credits', icon: 'i-solar:wallet-money-bold-duotone' },
          { label: 'Privacy boundary', to: '/help/privacy', icon: 'i-solar:lock-keyhole-bold-duotone' },
        ],
      },
      '/resources': {
        label: 'Characters & Resources',
        eyebrow: 'Culture Resources',
        title: 'Publishing characters and resources requires clear consent',
        summary: 'The resource page can browse and import, but publishing must show exactly what becomes public.',
        body: [
          'Public fields may include display name, description, tags, public avatar or cover, selected public persona text, resource files, license fields, creator display name, and version.',
          'Never publish chat history, memory, relationship state, API keys, apiBaseUrl, local paths, private notes, provider secrets, or unreviewed capability settings by default.',
        ],
        actions: [
          { label: 'Privacy help', to: '/help/privacy', icon: 'i-solar:shield-user-bold-duotone' },
          { label: 'Third-party notices', to: '/legal/third-party-notices', icon: 'i-solar:document-text-bold-duotone' },
        ],
      },
      '/changelog': {
        label: 'Changelog',
        eyebrow: 'Release Notes',
        title: 'July 20, 2026 update',
        summary: 'A focused update to accounts, official cloud, and desktop quick chat.',
        body: [
          'The account page now includes a credit refresh action so balances can be synchronized immediately after sign-in or redemption.',
          'The official-cloud model path has been upgraded and moved to the Hong Kong deployment for more reliable connectivity and clearer errors.',
          'Quick Chat now treats minimize and close as separate actions. Existing replies remain visible after expansion, and the next input renders normally.',
          'Live2D reply bubbles now appear above and to the left of the character, reducing face obstruction while remaining inside the display.',
        ],
        actions: [
          { label: 'Download', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: 'Installer help', to: '/help/installer', icon: 'i-solar:shield-check-bold-duotone' },
        ],
      },
      '/help/installer': {
        label: 'Installer Help',
        eyebrow: 'Help',
        title: 'Install, update, uninstall, and recover',
        summary: 'Installer help should work for ordinary Windows users, not only developers.',
        body: [
          'Download the complete installer from this site and run it directly. After installation, launch it from the desktop shortcut or Start menu.',
          'Because the current QA installer is unsigned, trusted testers must be told Windows can warn or block the installer.',
        ],
        actions: [
          { label: 'Download', to: '/download', icon: 'i-solar:download-square-bold-duotone' },
          { label: 'Refund support', to: '/help/refund', icon: 'i-solar:hand-money-bold-duotone' },
        ],
      },
      '/help/privacy': {
        label: 'Privacy Help',
        eyebrow: 'Help',
        title: 'Local-first privacy boundary',
        summary: 'Users should know what stays local and what a cloud request sends before payment.',
        body: [
          'Chat history stays local by default. Long-term memory stays local by default. Relationship state stays local by default. Local paths and Workbench project content are not uploaded by default.',
          'Official-cloud requests send prompts and necessary context for the current request. Account, credits, ledger, entitlements, activation codes, and usage events are stored server-side for login, commerce, support, and abuse control.',
        ],
        actions: [
          { label: 'Privacy policy', to: '/legal/privacy', icon: 'i-solar:document-text-bold-duotone' },
          { label: 'Terms', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
      '/help/refund': {
        label: 'Refund & Support',
        eyebrow: 'Help',
        title: 'Refunds, compensation, and manual support',
        summary: 'Automatic refunds are not open during Beta; support handles payment and credit issues manually.',
        body: [
          'Wuwiii Beta does not offer automatic refunds. Refund and compensation requests are handled manually through the published support contact, with account id, order evidence, and relevant usage or ledger details.',
          'Unused paid credits, failed deductions, and model-call issues are reviewed by support. Resolution is manual compensation, ledger correction, or refund handling.',
          'Support contact: email 3222708890@qq.com, QQ 3222708890, WeChat Hehaolingdang123.',
          'Support normally acknowledges a request within 7 calendar days and reports the outcome of complex cases within 15 calendar days. Mandatory rights under applicable law take precedence.',
          'Credit-deduction support should use account ledger evidence, usage event trace ids, and Wuwiii gateway refund behavior rather than direct database edits.',
        ],
        actions: [
          { label: 'Credits', to: '/account/credits', icon: 'i-solar:wallet-money-bold-duotone' },
          { label: 'Terms', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
      '/legal/privacy': {
        label: 'Privacy',
        eyebrow: 'Legal',
        title: 'Wuwiii Beta Privacy Policy',
        summary: 'Effective July 25, 2026. The current Beta is operated through the wuwiii.cn operator contact.',
        body: [
          'Wuwiii keeps chat history, long-term memory, relationship state, local paths, user API keys, and Workbench project content local by default.',
          'Server-side records include account, session, credits, ledger, activation codes, entitlements, orders, official-cloud usage events, explicitly published resources, moderation records, and user-submitted support diagnostics.',
          'Official-cloud requests route prompts and necessary context for the current request through the Wuwiii gateway to Wuwiii services and upstream model providers.',
          'Users can clear local chat, memory, and settings in the app. To request deletion of server-side account data, contact support with an account identifier and complete identity verification. Active sessions are revoked and data that is no longer required is deleted or anonymized within 30 days after verification.',
          'Order, credit-ledger, security-audit, and dispute records are retained only for periods necessary under applicable law, financial audit, fraud prevention, or dispute handling, with restricted access. Support diagnostics are not reused for unrelated purposes after the case is closed.',
          'The public paid Beta is currently for users aged 18 or older. Minors must not register or purchase the Beta service. Privacy and deletion requests can be sent to 3222708890@qq.com.',
        ],
        actions: [
          { label: 'Privacy help', to: '/help/privacy', icon: 'i-solar:shield-user-bold-duotone' },
          { label: 'Terms', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
      '/legal/terms': {
        label: 'Terms',
        eyebrow: 'Legal',
        title: 'Wuwiii Beta Terms',
        summary: 'Effective July 25, 2026. These terms cover early software, credits, support, resource publishing, and acceptable use.',
        body: [
          'Wuwiii Beta is early Beta software. Features, prices, credit costs, models, and availability may change during operation.',
          'By registering or purchasing, the user confirms that they are at least 18 years old and accepts the current privacy and refund-support terms. Credits are service metering units, not currency, and cannot be transferred, withdrawn, or traded outside the account.',
          'Users should not rely on Wuwiii for emergency, medical, legal, financial, or other high-stakes decisions. Public resource uploads must respect rights, licenses, and moderation rules.',
          'Users must not use the service for unlawful activity, infringement, bypassing billing or security controls, automated abuse, or distributing character, voice, model, or media assets without permission. Accounts may be restricted, sessions revoked, or service stopped for violations, with audited operator actions.',
          'Service failures, failed deductions, and order disputes follow the refund-support process. Mandatory consumer rights under applicable law are not excluded by these terms.',
        ],
        actions: [
          { label: 'Refund support', to: '/help/refund', icon: 'i-solar:hand-money-bold-duotone' },
          { label: 'Privacy', to: '/legal/privacy', icon: 'i-solar:document-text-bold-duotone' },
        ],
      },
      '/legal/third-party-notices': {
        label: 'Third-Party Notices',
        eyebrow: 'Legal',
        title: 'Third-party assets, libraries, and model notices',
        summary: 'Source code, dependencies, and user-imported assets remain subject to their respective licenses and terms.',
        body: [
          'This project continues development from Project AIRI source code under the MIT License. Distributions must retain the original copyright notice and MIT License. Libraries, fonts, and icons remain subject to their own licenses.',
          'Importing a character card, Live2D/VRM model, voice, motion, background, or other asset does not grant a new license. Users must verify their rights to use, modify, display, and redistribute imported assets.',
          'Commercial builds may bundle only assets with recorded source, author, license, commercial-use permission, and redistribution permission. Default characters, voices, or images with unclear status must not ship in the commercial build.',
          'The reviewed AvatarSample_A and AvatarSample_B files are provided by VRoid Project; their embedded metadata permits commercial use and redistribution. The bundled notices for Hiyori Free/Pro require acceptance of the applicable Live2D terms and impose enterprise-size limits. Both Hiyori files remain on commercial release hold until the redistribution basis is archived.',
          'The complete third-party inventory and license texts must be archived with each final installer version. This page does not treat any unlisted asset as commercially cleared.',
        ],
        actions: [
          { label: 'Resources', to: '/resources', icon: 'i-solar:folder-with-files-bold-duotone' },
          { label: 'Terms', to: '/legal/terms', icon: 'i-solar:document-add-bold-duotone' },
        ],
      },
    },
  },
} as const satisfies Record<BetaPublicLocale, BetaPublicCopy>

export function getBetaPublicNavigation(locale: BetaPublicLocale): readonly BetaPublicNavigationItem[] {
  return betaPublicContent[locale].navigation
}

export function getBetaPublicPage(routePath: BetaPublicPageRoute, locale: BetaPublicLocale): BetaPublicPage {
  return betaPublicContent[locale].pages[routePath]
}

export const betaPublicNavigation = betaPublicContent['zh-Hans'].navigation
export const betaPublicPages = betaPublicContent['zh-Hans'].pages
